import clsx from 'clsx'
import compact from 'lodash/compact'
import first from 'lodash/first'
import map from 'lodash/map'
import React, { useMemo } from 'react'

import { BlockContainer } from '@Components/BlockContainer'
import { Image } from '@Components/CMS/Image'
import { Product as ImageProduct } from '@Components/CMS/Image/ProductsAndCaption'
import {
  Block,
  ImageResource,
  PagePresentation,
  Product,
} from '@Generated/graphql'
import { BlockContext } from '@Globals/CMS/Context'
import {
  getJSONFieldValue,
  getLinkFieldValue,
  getLinkType,
  getObjectFieldValue,
  getPageReferences,
  getProductFieldValue,
  getReferenceFieldValue,
} from '@Globals/CMS/FieldHelpers'
import { convertGraphQLPins, Image as ImageData } from '@Globals/Image'

import styles from './ImageBlock.module.scss'

const imageWidthToImageType = (type: string): 'column' | 'fullScreen' | 'fullWidth' => {
  switch (type) {
    case 'Column':
      return 'column'
    case 'Full screen':
      return 'fullScreen'
    case 'Full page':
      return 'fullWidth'
    default:
      return 'column'
  }
}

const imageHeightToMaxHeight = (height: string): number | null => {
  if (!height || height === 'Auto') {
    return null
  }

  return +height.substring(0, height.length - 1)
}

interface ImageBlockData {
  caption: string
  crop: 'Top' | 'Center' | 'Bottom'
  height?: number
  image: ImageData
  link: string
  linkNewTab: boolean
  linkPageID: string
  linkPageType: string
  mobileImage: ImageData
  products: ImageProduct[]
  type: 'column' | 'fullScreen' | 'fullWidth'
}

const productToImageProduct = (product: Product): ImageProduct => ({
  id: product.id,
  link: product.url || '',
  title: product.name || '',
})

const getBlockData = (imageBlock: Block, isInModal: boolean): ImageBlockData => {
  const settings = getObjectFieldValue(imageBlock.fields || [], 'settings') || []
  const imageResource = first(getReferenceFieldValue<ImageResource>(imageBlock.fields || [], 'image'))
  const captionOptions = imageResource?.captionOptions
  const link = imageResource ? getLinkFieldValue(imageResource.fields || [], 'link') : null
  const captionPageReferences = getPageReferences(captionOptions?.references || [])
  const products = compact(
    map(
      captionPageReferences,
      (x) => {
        const product = getProductFieldValue(x.fields || [], 'product')
        if (!product) {
          return undefined
        }

        return productToImageProduct(product)
      },
    ),
  )

  const image: ImageData = {
    dimensions: {
      height: imageResource?.image?.dimensions?.height || 0,
      width: imageResource?.image?.dimensions?.width || 0,
    },
    pins: convertGraphQLPins(imageResource?.image?.pins),
    url: imageResource?.image?.url || '',
  }

  const mobileImage: ImageData = {
    dimensions: {
      height: imageResource?.mobileImage?.dimensions?.height || 0,
      width: imageResource?.mobileImage?.dimensions?.width || 0,
    },
    pins: convertGraphQLPins(imageResource?.mobileImage?.pins),
    url: imageResource?.mobileImage?.url || '',
  }

  if (isInModal && mobileImage.url) {
    image.dimensions = mobileImage.dimensions
    image.pins = mobileImage.pins
    image.url = mobileImage.url
  }

  const reference = first(link?.references)
  const referenceID = reference && 'id' in reference ? reference.id || '' : ''

  return {
    caption: captionOptions?.caption || '',
    // TODO: Check default value for crop
    crop: getJSONFieldValue(settings, 'cropping') || 'Center',
    height: imageHeightToMaxHeight(getJSONFieldValue(settings, 'height') || '') || undefined,
    image,
    link: link?.url || '',
    linkNewTab: link?.newTab || false,
    linkPageID: referenceID,
    linkPageType: link ? getLinkType(link) : '',
    mobileImage,
    products,
    type: imageWidthToImageType(getJSONFieldValue(settings, 'width') || ''),
  }
}

const ImageBlock = ({
  data,
  id,
  index,
}: {
  data: Block
  id?: string
  index?: number
}) => {
  const { isInjection, presentation, shouldFillContainer } = React.use(BlockContext)
  const {
    caption,
    crop,
    height,
    image: desktopImage,
    link,
    linkNewTab,
    linkPageID,
    linkPageType,
    mobileImage,
    products,
    type,
  } = useMemo(() => getBlockData(data, presentation === PagePresentation.Modal), [data, presentation])

  const { appearance, common } = data

  if (!desktopImage?.url) {
    return null
  }

  return (
    <BlockContainer
      appearance={appearance || {}}
      common={common || {}}
      commonWidth={(common?.position === 'left' || common?.position === 'right') ? 'half' : undefined}
      heightRestriction
      id={id}
      noPadding={presentation === PagePresentation.Full}
      style={isInjection ? { maxHeight: '100%', height: '100%' } : undefined}
    >
      <Image
        caption={caption}
        className={clsx(isInjection && styles.imageBlockInjection)}
        crop={crop}
        desktopImage={desktopImage}
        desktopMaxHeight={height}
        destPageType={linkPageType}
        destPageTypeId={linkPageID}
        link={link}
        linkNewTab={linkNewTab}
        mobileImage={mobileImage.url ? mobileImage : undefined}
        overlay={common?.position === 'within'}
        priority={index === 0}
        products={products}
        type={common?.position === 'within' || shouldFillContainer ? 'fillContainer' : type}
      />
    </BlockContainer>
  )
}

export default ImageBlock
